import React, { useMemo, useState } from 'react';
import { Worlds, errorMessage } from '../../api/client';
import { TrustedPlayer, World } from '../../types';
import { Icon } from '../ui/Icon';
import { Confirm, Empty, Loading, Note, PlayerHead, Section } from '../ui/primitives';
import { shortId } from '../../utils/format';

interface Props {
  world: World;
  trusted: TrustedPlayer[] | null;
  error: string | null;
  onChange: (trusted: TrustedPlayer[]) => void;
  /** Viewer can see the list but not change it. */
  readonly?: boolean;
}

const USERNAME_RE = /^[A-Za-z0-9_]{1,16}$/;
export const trustedName = (p: TrustedPlayer): string => p.username || shortId(p.uuid);

/** Trusted players: who can join and build. Adds by Minecraft username, removes with confirmation. */
const PlayersTab: React.FC<Props> = ({ world, trusted, error, onChange, readonly }) => {
  const [input, setInput] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [justAdded, setJustAdded] = useState<string | null>(null);
  const [pendingRemove, setPendingRemove] = useState<TrustedPlayer | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const list = useMemo(() => [...(trusted || [])].sort((a, b) => trustedName(a).localeCompare(trustedName(b))), [trusted]);
  const valid = USERNAME_RE.test(input.trim());

  const add = async () => {
    const username = input.trim();
    if (!valid || adding) return;
    setAdding(true);
    setAddError(null);
    setJustAdded(null);
    try {
      const player = await Worlds.addTrusted(world.id, username);
      onChange([...(trusted || []).filter((p) => p.uuid !== player.uuid), player]);
      setInput('');
      setJustAdded(trustedName(player));
    } catch (e) {
      setAddError(errorMessage(e, `Could not trust ${username}. Please try again.`));
    } finally {
      setAdding(false);
    }
  };

  const remove = async () => {
    if (!pendingRemove) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      await Worlds.removeTrusted(world.id, pendingRemove.uuid);
      onChange((trusted || []).filter((p) => p.uuid !== pendingRemove.uuid));
      setPendingRemove(null);
    } catch (e) {
      setRemoveError(errorMessage(e, 'Could not remove that player. Please try again.'));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Section title="Trusted players" blurb="They can join and build, but can't change world settings.">
      {!readonly && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              className="input input--mono"
              placeholder="minecraft username…"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setAddError(null);
                setJustAdded(null);
              }}
              onKeyDown={(e) => e.key === 'Enter' && add()}
              style={{ width: 260 }}
              maxLength={16}
              disabled={adding}
              aria-label="Minecraft username to trust"
            />
            <button type="button" className="btn btn--primary" onClick={add} disabled={!valid || adding}>
              {adding ? <span className="spin" style={{ borderTopColor: '#fff' }} /> : <Icon name="plus" size={12} />} Trust player
            </button>
            {input && !valid && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Usernames are 1–16 letters, numbers or underscores.</span>}
          </div>
          <small style={{ display: 'block', marginTop: 8, fontSize: 11.5, color: 'var(--ink3)' }}>
            They need to have joined the Bakery at least once. Trusted players get access straight away, even while the world is running.
          </small>
          {addError && (
            <div style={{ marginTop: 12 }}>
              <Note tone="danger">{addError}</Note>
            </div>
          )}
          {justAdded && !addError && (
            <div style={{ marginTop: 12 }}>
              <Note tone="ok">{justAdded} is now trusted on {world.name}.</Note>
            </div>
          )}
        </div>
      )}

      {error && <Note tone="danger">{error}</Note>}
      {trusted == null && !error ? (
        <Loading />
      ) : list.length === 0 ? (
        <Empty icon="users" title="No trusted players yet">
          {readonly ? 'Only the owner can invite players.' : 'Invite friends so they can join and build with you.'}
        </Empty>
      ) : (
        <div className="heads">
          {list.map((p) => (
            <span className="hcard" key={p.uuid} title={p.uuid}>
              <PlayerHead uuid={p.uuid} size={26} />
              {trustedName(p)}
              {!readonly && (
                <button type="button" className="x" title={`Remove ${trustedName(p)}`} onClick={() => setPendingRemove(p)}>
                  <Icon name="x" size={11} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <Confirm
        open={!!pendingRemove}
        title={pendingRemove ? `Remove ${trustedName(pendingRemove)}?` : ''}
        body={`They will lose access to ${world.name} immediately and be kicked if they're playing right now.`}
        confirmLabel="Remove player"
        danger
        busy={removing}
        error={removeError}
        onCancel={() => {
          if (!removing) {
            setPendingRemove(null);
            setRemoveError(null);
          }
        }}
        onConfirm={remove}
      />
    </Section>
  );
};

export default PlayersTab;
